import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, KeyRound, User as UserIcon, Phone, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { trackSignupConversion, getUtm } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { CreceLogo } from "@/components/CreceLogo";

const TESTIMONIALS = [
  ["I started part-time alongside my job. The free training and ready leads meant I closed my first client in week one.", "Consultant, Pune"],
  ["PolicyLens does the explaining for me — clients trust what they can see. My referrals doubled.", "Consultant, Jaipur"],
  ["The 10-grade career plan keeps me pushing. My team now earns for me even on my off days.", "Senior Consultant, Indore"],
];

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { partner_label } = useSettings();
  const labelP = partner_label || "Agent";
  const [mode, setMode] = useState("login"); // login | signup | otp
  const [quoteIdx, setQuoteIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setQuoteIdx((i) => (i + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(t);
  }, []);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [isCompany, setIsCompany] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [otp, setOtp] = useState("");
  const [otpDevHint, setOtpDevHint] = useState("");
  const [resendIn, setResendIn] = useState(0); // seconds until resend allowed
  const [loading, setLoading] = useState(false);
  const resendTimer = useRef(null);

  useEffect(() => {
    const r = searchParams.get("ref");
    if (r) { setReferralCode(r.toUpperCase()); setMode("signup"); }
    if (searchParams.get("mode") === "signup") setMode("signup");
  }, [searchParams]);

  useEffect(() => () => { if (resendTimer.current) clearInterval(resendTimer.current); }, []);

  const startResendCooldown = (secs = 30) => {
    setResendIn(secs);
    if (resendTimer.current) clearInterval(resendTimer.current);
    resendTimer.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) { clearInterval(resendTimer.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (mobile.replace(/\D/g, "").length !== 10) { toast.error("Mobile must be 10 digits"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup-request-otp", {
        name: name.trim(), email: email.trim().toLowerCase(),
        password, mobile: mobile.replace(/\D/g, ""),
        referral_code: referralCode.trim() || null,
        utm: getUtm(),
      });
      setMode("otp");
      setOtp("");
      setOtpDevHint(data.dev_otp || "");
      startResendCooldown(30);
      toast.success("We've sent a 6-digit code to your email.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send code");
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    if (resendIn > 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup-resend-otp", { email: email.trim().toLowerCase() });
      setOtpDevHint(data.dev_otp || "");
      startResendCooldown(30);
      toast.success("A new code has been sent to your email.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't resend code");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    if (e) e.preventDefault();
    if (otp.replace(/\D/g, "").length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup-verify-otp", {
        email: email.trim().toLowerCase(), otp: otp.replace(/\D/g, ""),
      });
      if (isCompany && companyName.trim()) localStorage.setItem("crece_company_request", JSON.stringify({ company_name: companyName.trim(), employees_count: Number(companySize) || null }));
      login(data.token, data.user);
      trackSignupConversion();
      toast.success(`Welcome to Crece, ${data.user.name}!`);
      navigate("/app/dashboard");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't verify code");
    } finally { setLoading(false); }
  };

  const loginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/partner-login", { email: email.trim().toLowerCase(), password });
      login(data.token, data.user);
      toast.success("Welcome back!");
      navigate("/app/dashboard");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong");
    } finally { setLoading(false); }
  };

  // Tagline copy by mode
  const tag = mode === "otp" ? "Verify your email" : (mode === "signup" ? `Become an ${labelP}` : "Welcome back");
  const heading = mode === "otp" ? "Check your inbox" : (mode === "signup" ? "Create your account" : "Sign in to Crece");
  const sub = mode === "otp"
    ? <>We sent a 6-digit code to <b className="text-stone-800">{email}</b>. It expires in 10 minutes.</>
    : (mode === "signup" ? `Set up your ${labelP.toLowerCase()} account in 30 seconds.` : "Use your email and password.");

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-emerald-900 via-emerald-950 to-stone-900 text-white p-12 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="relative z-10 flex items-center gap-3">
          <CreceLogo size="md" theme="light" />
          <div className="font-display font-black text-2xl tracking-tight">Crece</div>
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-[0.3em] mb-4"><Sparkles className="w-3.5 h-3.5" /> Earn while you refer</div>
          <h1 className="font-display text-4xl xl:text-5xl font-black tracking-tight leading-[1.05]">Your side-income,<br/>built like a career.</h1>
          <div className="mt-6 grid grid-cols-3 gap-3 max-w-md" data-testid="auth-how-it-works">
            {[["1", "Sign up free", "2 minutes, zero fees"], ["2", "Get trained", "Free live sessions + certificate"], ["3", "Refer & earn", "Weekly payouts to your bank"]].map(([n, t, d]) => (
              <div key={n} className="bg-white/5 border border-white/10 rounded-xl p-3 backdrop-blur">
                <div className="w-6 h-6 rounded-full bg-amber-400 text-emerald-950 grid place-items-center text-xs font-black">{n}</div>
                <div className="text-sm font-bold mt-2 leading-tight">{t}</div>
                <div className="text-[11px] text-stone-400 mt-0.5 leading-snug">{d}</div>
              </div>
            ))}
          </div>
          <ul className="mt-6 space-y-3 max-w-md" data-testid="auth-why-join">
            {[
              ["₹0 to start", "no joining fees, no targets, no paperwork."],
              ["Leads handed to you", "pick ready clients from the Growth Exchange."],
              ["AI tools that sell for you", "PolicyLens decoder, branded brochures, marketing studio."],
              ["Build a team, multiply income", "earn on your team's success across 10 career grades."],
            ].map(([t, d]) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-amber-400 text-emerald-950 grid place-items-center flex-shrink-0 text-[11px] font-black">✓</span>
                <span className="text-sm leading-snug"><b className="text-white">{t}</b><span className="text-stone-300"> — {d}</span></span>
              </li>
            ))}
          </ul>
          <div className="mt-7 bg-white/5 border border-amber-400/30 rounded-2xl p-4 max-w-md" data-testid="auth-testimonial">
            <div className="text-amber-400 text-lg leading-none">“</div>
            <p className="text-sm text-stone-200 italic leading-snug -mt-1">{TESTIMONIALS[quoteIdx][0]}</p>
            <div className="text-xs text-amber-300 font-bold mt-2">{TESTIMONIALS[quoteIdx][1]}</div>
          </div>
          <div className="mt-6 flex gap-6 text-center" data-testid="auth-stats-strip">
            {[["10", "career grades"], ["Weekly", "payouts"], ["₹0", "investment"], ["100%", "free training"]].map(([v, l]) => (
              <div key={l}>
                <div className="font-display text-2xl font-black text-amber-400">{v}</div>
                <div className="text-[10px] uppercase tracking-wider text-stone-400 font-bold">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-stone-400">A platform by <b className="text-stone-300">Crece Infotech Private Limited</b> · Products issued by regulated partner institutions · No income guarantees, commissions per published rate cards.</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 lg:hidden mb-6">
            <CreceLogo size="sm" theme="dark" /><span className="font-display font-black text-xl">Crece</span>
          </div>
          {mode === "signup" && (
            <div className="lg:hidden mb-6" data-testid="auth-mobile-pitch">
              <div className="bg-emerald-950 text-white rounded-2xl p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-400">Why 1000s are joining Crece</div>
                <div className="font-display text-lg font-black mt-1 leading-tight">Refer financial products. Earn weekly. Grow a team.</div>
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1 -mx-1 px-1">
                  {["₹0 to start", "Weekly payouts", "Free training", "Ready leads", "AI selling tools", "10-grade career"].map((c) => (
                    <span key={c} className="flex-shrink-0 text-[11px] font-bold bg-white/10 border border-white/15 rounded-full px-3 py-1.5">{c}</span>
                  ))}
                </div>
                <div className="text-[10px] text-stone-400 mt-2.5">By Crece Infotech Private Limited · Products by regulated partners</div>
              </div>
            </div>
          )}
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">{tag}</span>
          <h2 className="font-display text-4xl font-black tracking-tight mt-2">{heading}</h2>
          <p className="text-stone-500 mt-2 text-sm">{sub}</p>

          {/* ----- OTP STEP ----- */}
          {mode === "otp" && (
            <form onSubmit={verifyOtp} className="mt-7 space-y-4" data-testid="auth-otp-form">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-900 text-sm flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  Email verification keeps your wallet & payouts secure. Enter the code from your inbox to finish creating your account.
                </div>
              </div>
              <div>
                <Label className="font-semibold text-sm">6-digit code</Label>
                <Input
                  data-testid="auth-otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="••••••"
                  className="mt-1.5 h-14 rounded-lg border-stone-300 text-center text-2xl font-mono font-bold tracking-[0.6em]"
                  autoFocus
                />
              </div>
              {otpDevHint && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800" data-testid="auth-otp-dev-hint">
                  <b>Dev mode:</b> Code is <span className="font-mono font-bold">{otpDevHint}</span> (email not yet configured)
                </div>
              )}
              <Button type="submit" data-testid="auth-otp-submit" disabled={loading}
                className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
                {loading ? "Verifying..." : "Verify & create account"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={() => setMode("signup")} className="text-stone-500 hover:text-emerald-900" data-testid="auth-otp-back">
                  ← Edit details
                </button>
                <button type="button" onClick={resendOtp} disabled={resendIn > 0 || loading}
                  className="font-bold text-emerald-900 hover:underline disabled:text-stone-400 disabled:no-underline"
                  data-testid="auth-otp-resend">
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}

          {/* ----- LOGIN + SIGNUP STEPS ----- */}
          {mode !== "otp" && (
            <form onSubmit={mode === "signup" ? requestOtp : loginSubmit} className="mt-7 space-y-4" data-testid="auth-form">
              {mode === "signup" && (
                <div>
                  <Label className="font-semibold text-sm">Full Name</Label>
                  <div className="relative mt-1.5">
                    <UserIcon className="absolute left-3 top-3.5 w-4 h-4 text-stone-500" />
                    <Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Riya Sharma"
                      className="pl-10 h-12 rounded-lg border-stone-300" />
                  </div>
                </div>
              )}
              <div>
                <Label className="font-semibold text-sm">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-3.5 w-4 h-4 text-stone-500" />
                  <Input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                    placeholder="you@example.com"
                    className="pl-10 h-12 rounded-lg border-stone-300" />
                </div>
              </div>
              {mode === "signup" && (
                <div>
                  <Label className="font-semibold text-sm">Mobile (for support & WhatsApp)</Label>
                  <div className="relative mt-1.5">
                    <Phone className="absolute left-3 top-3.5 w-4 h-4 text-stone-500" />
                    <Input data-testid="auth-mobile" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} required placeholder="9876543210" maxLength={10} inputMode="numeric"
                      className="pl-10 h-12 rounded-lg border-stone-300" />
                  </div>
                </div>
              )}
              <div>
                <Label className="font-semibold text-sm">Password</Label>
                <div className="relative mt-1.5">
                  <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-stone-500" />
                  <Input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                    placeholder={mode === "signup" ? "Min 8 chars, 1 letter + 1 digit" : "Your password"}
                    className="pl-10 h-12 rounded-lg border-stone-300" />
                </div>
                {mode === "login" && (
                  <div className="text-right mt-1.5">
                    <Link to="/auth/forgot" className="text-xs font-bold text-emerald-900 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                  </div>
                )}
              </div>
              {mode === "signup" && (
                <div>
                  <Label className="font-semibold text-sm">Referral Code <span className="font-normal text-stone-400">(optional)</span></Label>
                  <Input data-testid="auth-referral" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="ABC12345"
                    className="mt-1.5 h-12 rounded-lg border-stone-300 font-mono uppercase" />
                  <label className="mt-4 flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid="auth-company-toggle-label">
                    <input data-testid="auth-company-toggle" type="checkbox" checked={isCompany} onChange={(e) => setIsCompany(e.target.checked)} className="w-4 h-4" />
                    I'm registering a company (corporate account with employee logins)
                  </label>
                  {isCompany && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <Input data-testid="auth-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Company name" className="col-span-2 h-11 rounded-lg border-stone-300" />
                      <Input data-testid="auth-company-size" type="number" min={1} value={companySize} onChange={(e) => setCompanySize(e.target.value)} placeholder="Employees" className="h-11 rounded-lg border-stone-300" />
                    </div>
                  )}
                  {isCompany && <p className="text-[11px] text-stone-500 mt-1">You start as a normal agent; admin reviews and upgrades you to a corporate account.</p>}
                </div>
              )}
              <Button type="submit" data-testid="auth-submit" disabled={loading}
                className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
                {loading ? "Please wait..." : (mode === "signup" ? "Send verification code" : "Sign in")}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          )}

          {mode !== "otp" && (
            <div className="mt-6 text-center text-sm text-stone-600">
              {mode === "signup" ? (
                <>Already have an account? <button onClick={() => setMode("login")} className="font-bold text-emerald-900 hover:underline" data-testid="switch-to-login">Sign in</button></>
              ) : (
                <>New to Crece? <button onClick={() => setMode("signup")} className="font-bold text-emerald-900 hover:underline" data-testid="switch-to-signup">Create an account</button></>
              )}
            </div>
          )}
          <div className="mt-4 text-center">
            <Link to="/" className="text-xs text-stone-500 hover:text-emerald-900">← Back to home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
